import { html } from 'lit';
/*
 * ONE avatar, used by every roster.
 *
 * Their photo when it loads, their initials when it does not — and the second
 * half is the point. The photo comes from the identity provider's CDN, which is a
 * third origin that can be blocked, rate-limited or simply unreachable from one
 * of our hosts and not another; that is exactly what happened, and the row
 * rendered an EMPTY grey disc, which reads as "this person has no name" rather
 * than "the picture did not arrive".
 *
 * So a failed image is not an error state, it is the fallback state: the initials
 * were always the right answer when there is no picture, and a picture that fails
 * to load is a picture there isn't.
 */
/*
 * URLs that have already failed. Module-level and never cleared, deliberately: a
 * CDN URL that failed once fails again for the same reason, and retrying it per
 * render would restart a request we know the outcome of on every keystroke in the
 * filter box.
 */
const broken = new Set();
/** Two letters from a display name — the same rule every roster used inline. */
export function initialsOf(name) {
    const letters = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
    // A name we cannot initial at all still needs a mark, or the row's left edge
    // collapses and the list loses its rhythm.
    return letters || '?';
}
/**
 * `onBroken` is called when the image fails, so the host can re-render into the
 * initials branch. It is a callback rather than internal state because the host
 * owns its own update cycle — this module should not reach into it.
 */
export function memberAvatar(name, imageUrl, onBroken) {
    if (imageUrl && !broken.has(imageUrl)) {
        return html `<img
      class="rbac-avatar rbac-avatar-img"
      src=${imageUrl}
      alt=""
      aria-hidden="true"
      referrerpolicy="no-referrer"
      @error=${() => {
            broken.add(imageUrl);
            onBroken();
        }}
    />`;
    }
    return html `<span class="rbac-avatar" aria-hidden="true">${initialsOf(name)}</span>`;
}
//# sourceMappingURL=member-avatar.js.map